import * as _ from "lodash";
import PropTypes from "prop-types";
import {
    type ReactNode,
    type Ref,
    useCallback,
    useImperativeHandle,
    useMemo,
    useRef,
} from "react";
import styled from "styled-components";

import { Button } from "@/components";

import FormContext from "./FormContext";
import FormControl from "./FormControl";

const StyledCloseButton = styled(Button)`
    && {
        margin-right: auto;
    }
`;

interface FormControlRef {
    validate: (value: unknown) => boolean;
    name: string;
}

interface FormProps {
    className?: string;
    onSubmit?: () => void;
    onValidate?: (
        controls: Record<string, unknown>,
        model: Record<string, unknown>,
    ) => boolean;
    model: Record<string, unknown>;
    children?: ReactNode;
    hideSubmit?: boolean;
    submitText?: string;
    submitDisable?: boolean;
    cancelText?: string;
    onCancel?: () => void;
    ref?: Ref<unknown>;
}

/**
 * Form wrapper that collects child controls and validates them on submit.
 *
 * @param root0 - The component props. `ref` exposes `validate()` and
 *   `addControl()`.
 * @returns The rendered form.
 */
const Form = ({
    className = "",
    onSubmit,
    onValidate,
    model,
    children,
    hideSubmit = false,
    submitText = "Save Changes",
    submitDisable = false,
    cancelText = "Cancel",
    onCancel,
    ref,
}: FormProps) => {
    const controlsRef = useRef<Record<string, FormControlRef>>({});

    const addControl = useCallback((id: string, control: FormControlRef) => {
        controlsRef.current = {
            ...controlsRef.current,
            [id]: control,
        };
    }, []);

    const contextValue = useMemo(() => ({ addControl }), [addControl]);

    const validateControls = () => {
        let isValid = true;
        _.forEach(controlsRef.current, (control, id) => {
            const value = model[id];
            if (!control.validate(value)) {
                isValid = false;
            }
        });
        return isValid;
    };

    const validate = () => {
        return !onValidate
            ? validateControls()
            : onValidate(controlsRef.current, model);
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (validate() && onSubmit) onSubmit();
    };

    // Expose validate and addControl via ref
    useImperativeHandle(ref, () => ({
        validate,
        addControl,
    }));

    return (
        <FormContext value={contextValue}>
            <form
                className={`lights-map-form ${className}`}
                noValidate
                onSubmit={handleSubmit}
            >
                <div className="lights-map-form-content">{children}</div>
                {!hideSubmit && (
                    <div className="lights-map-form-button">
                        <Button type="submit" disabled={submitDisable}>
                            {submitText}
                        </Button>
                        {onCancel && (
                            <StyledCloseButton onClick={onCancel} outlined>
                                {cancelText}
                            </StyledCloseButton>
                        )}
                    </div>
                )}
            </form>
        </FormContext>
    );
};

Form.FormControl = FormControl;

Form.propTypes = {
    className: PropTypes.string,
    onSubmit: PropTypes.func,
    onValidate: PropTypes.func,
    model: PropTypes.any.isRequired,
    children: PropTypes.node,
    hideSubmit: PropTypes.bool,
    submitText: PropTypes.string,
    submitDisable: PropTypes.bool,
    cancelText: PropTypes.string,
    onCancel: PropTypes.func,
};

export default Form;
